import { ReactNode, createElement } from 'react';

import { PropsBridgeProvider } from '../../context/PropsBridgeContext';
import type { PageContentProps } from './Content';
import { PageImpressionWrapper, usePageRootChildren } from './Wrapper';
import { PageRootBridgeProps, PageRootProps } from './types';
import {
  getPageDescriptor,
  useBreadcrumbAncestors,
  useOptionalPageBridgeProps,
} from './utils';

export type RootProps = PageRootProps & {
  title?: ReactNode;
  children: ReactNode;
};

export const Root = ({ routeRef, title, children }: RootProps) => {
  const parentBridgeProps = useOptionalPageBridgeProps();

  const descriptor = getPageDescriptor(routeRef);
  const ancestors = useBreadcrumbAncestors(routeRef);
  const { contentElement, wrapperElements } = usePageRootChildren(children);

  const contentProps = contentElement?.props as PageContentProps | undefined;
  const narrow = contentProps?.narrow;

  const wrapped = wrapperElements.reduceRight<ReactNode>(
    (acc, { element, component: typedComponent }) => {
      if (typedComponent) {
        return createElement(typedComponent, element.props, acc);
      }
      const { component, ...rest } = element.props;
      return createElement(component, rest, acc);
    },
    contentElement,
  );

  return (
    <PageImpressionWrapper descriptor={descriptor}>
      <PropsBridgeProvider<PageRootBridgeProps>
        {...(parentBridgeProps ?? {})}
        descriptor={descriptor}
        ancestors={ancestors}
        leafLabel={title !== undefined ? title : descriptor.title}
        wrapperElements={wrapperElements}
        narrow={narrow}
      >
        {wrapped}
      </PropsBridgeProvider>
    </PageImpressionWrapper>
  );
};
